import { type HttpContext } from '@angular/common/http';
import { mailboxTenantContext } from '@modules/common/interceptors/spaces.interceptor';
import { CalendarTenantScopeService } from '../../services/calendar-tenant-scope.service';
import { Component, inject, Input } from '@angular/core';
import { CalendarsService } from '@modules/calendar-app/services/calendars.service';
import { DefaultCalendarService } from '@modules/calendar-app/services/default-calendar.service';
import { EventsService } from '@modules/calendar-app/services/events.service';
import { RecurrenceEditModalService } from '@modules/calendar-app/services/recurrence-edit-modal.service';
import { CalendarAppStateService } from '@modules/calendar-app/services/state.service';
import { Calendar } from '@modules/calendar-app/types/calendar';
import { CalendarEvent } from '@modules/calendar-app/types/calendar-event';
import { CalendarsFilters } from '@modules/calendar-app/types/calendars-filters';
import { VirtualFolder } from '@modules/calendar-app/types/virtual-folder';
import { heightAnimation } from '@modules/common/animations/height.animation';
import { BaseSidebarContainersTreeComponent } from '@modules/common/components/base-sidebar-containers-tree/base-sidebar-containers-tree.component';
import { moveItemsIntoContainer } from '@modules/common/utils/move-into-container.util';
import { SideBarItemAction } from '@modules/common/components/sidebar-item/sidebar-item.component';
import { Application } from '@modules/common/types/application';
import { Stitch } from '@modules/common/types/stitch';
import { DragData, dragDataTypeAllowed, DragDataTypes, getStringField } from '@modules/drag-n-drop/types/drag-data';
import { StateKey } from '@modules/settings/types/state-key';
import type { CalendarSummaryDto } from '@sharedlib/dtos/dto/calendar-app/calendar.dto';
import type { CalendarEventSummaryDto } from '@sharedlib/dtos/dto/calendar-app/event.dto';
import { Objects } from '@sharedlib/common/lang/Objects';
import { ToasterService } from '@frontendlib/common/features/toaster/services/toaster.service';
import { CROSS_TENANT_DROP_TEXT } from '@modules/common/services/container-dnd.service';
import { Observable, of } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';

@Component({
  selector: 'app-side-bar-calendars-tree',
  templateUrl: './side-bar-calendars-tree.component.html',
  styleUrls: ['./side-bar-calendars-tree.component.less'],
  animations: [heightAnimation],
  standalone: false,
})
export class SideBarCalendarsTreeComponent extends BaseSidebarContainersTreeComponent<
  Calendar,
  CalendarEvent,
  CalendarsFilters,
  VirtualFolder
> {
  selfDragDataTypes = [DragDataTypes.event, DragDataTypes.calendar];
  containerDragDataType = DragDataTypes.calendar;
  application = Application.calendar;
  treeStateKey = StateKey.calendarSidebarCalendarsTree;

  private readonly recurrenceEditModalService = inject(RecurrenceEditModalService);

  protected actionsFor(calendar: Calendar): SideBarItemAction[] {
    return [
      {
        icon: 'sapphire-settings',
        tooltip: 'View calendar',
        click: (event: MouseEvent) => {
          event.stopPropagation();
          this.openItem(calendar);
        },
      },
    ];
  }

  public dndPredicate =
    (stitchItem: Stitch) =>
    (dragData: DragData): boolean =>
      stitchItem &&
      !(
        dragData.type === DragDataTypes.calendar &&
        dragData.data.length === 1 &&
        dragData.data?.[0]?.id === stitchItem?.id
      ) &&
      dragDataTypeAllowed(dragData.type) &&
      !this.isCrossTenantDrop(dragData, stitchItem);

  @Input() public tenantId: string | null | undefined;
  private readonly calendarScope = inject(CalendarTenantScopeService);
  private readonly defaultCalendarService = inject(DefaultCalendarService, { optional: true });

  protected override requestContext(): HttpContext | undefined {
    return this.tenantId === undefined ? undefined : mailboxTenantContext(this.tenantId);
  }

  protected override beforeContainerSelectionApplied(): void {
    if (this.tenantId !== undefined) this.calendarScope.selectTenant(this.tenantId);
  }

  private readonly eventsService = inject(EventsService);
  private readonly calendarAppStateService = inject(CalendarAppStateService);

  constructor(private readonly calendarsService: CalendarsService) {
    super(calendarsService);
  }

  protected override getSelectedItemUids(): Observable<string[]> {
    if (this.tenantId) return this.calendarAppStateService.getSelectedCalendarUidsForTenant(this.tenantId);
    return super
      .getSelectedItemUids()
      .pipe(switchMap((ids) => (ids?.length ? of(ids) : this.calendarAppStateService.getSelectedCalendarUids())));
  }

  protected override onItemsReceived() {
    const defaultCalendar = this.defaultCalendarService?.currentDefault?.();
    // The tree may still hold the empty response issued before the lazy default was created.
    // A storage emission must not turn the new default's seeded checkbox into a deliberate [].
    if (
      defaultCalendar &&
      this.selectedItemsIds.includes(defaultCalendar.id) &&
      !this.items.some(({ id }) => id === defaultCalendar.id)
    )
      return;
    const orderedSelection = this.items.map(({ id }) => id).filter((id) => this.selectedItemsIds.includes(id));
    if (!Objects.equals(orderedSelection, this.selectedItemsIds)) {
      this.calendarAppStateService.setSelectedCalendarUids(orderedSelection, this.tenantId ?? undefined);
    }
  }

  /** The calendars shown before the last click on a row flipped one, for a double-click on that row to put back. */
  private selectionBeforeClick: string[] | null = null;

  /**
   * Whether the last click the row itself received was the second of a double-click. A `dblclick` is the row's own
   * only then: a control inside the row (Options, the checkbox, the chevron) keeps its clicks from the row, but its
   * `dblclick` still bubbles up here, long after the row click the snapshot belongs to.
   */
  private secondClickOnRow = false;

  /** Every native click the row receives, whatever `appClick` makes of it. */
  protected noteRowClick(event: MouseEvent): void {
    this.secondClickOnRow = event.detail === 2;
  }

  /** A click on a row shows or hides its calendar at once; see `handleDblClick` for the double-click. */
  protected toggleOnClick(calendar: Calendar, checked: boolean): void {
    this.selectionBeforeClick = [...this.selectedItemsIds];
    this.onCalendarChecked(calendar, !checked);
  }

  /**
   * A double-click opens the calendar's form and leaves the calendar shown or hidden as it was. Its first click has
   * already flipped the check (a click acts at once), so the selection from before that click is written back — one
   * write, the last, never a second flip (loki, instantclick r1). Only for the row's own double-click: see
   * `secondClickOnRow`.
   */
  override handleDblClick(calendar: Calendar): void {
    const before = this.secondClickOnRow ? this.selectionBeforeClick : null;
    this.selectionBeforeClick = null;
    this.secondClickOnRow = false;
    if (before) {
      this.beforeContainerSelectionApplied();
      this.calendarAppStateService.setSelectedCalendarUids(before, this.tenantId ?? undefined);
    }
    super.handleDblClick(calendar);
  }

  protected onCalendarChecked(calendar: Calendar, checked: boolean): void {
    this.beforeContainerSelectionApplied();
    const targetSelection = checked
      ? [...this.selectedItemsIds, calendar.id]
      : this.selectedItemsIds.filter((id) => id !== calendar.id);

    const orderedSelection = this.items.map(({ id }) => id).filter((id) => targetSelection.includes(id));

    this.calendarAppStateService.setSelectedCalendarUids(orderedSelection, this.tenantId ?? undefined);

    // Unchecking leaves the last-activated id as is rather than clearing it: `getBestSelectedCalendarUid`
    // already ignores it once it drops out of the selection, and re-checking the same calendar right
    // after should still count as reactivating it rather than falling back to Default in between.
    if (checked) {
      this.calendarAppStateService.setLastActivatedCalendarUid(calendar.id, this.tenantId ?? undefined);
    }
  }

  private readonly toasterService = inject(ToasterService);

  private isCrossTenantDrop(dragData: DragData, item: Stitch): boolean {
    const destination = item.accountId ?? this.tenantId;
    return !!destination && dragData.data.some(source => {
      const owner = getStringField(source, 'accountId');
      return owner !== undefined && owner !== destination;
    });
  }

  public handleMove(dragData: DragData, item: Calendar) {
    if (this.isCrossTenantDrop(dragData, item)) {
      this.toasterService.show({ text: CROSS_TENANT_DROP_TEXT.moveTooltip });
      return;
    }
    const message = 'Successfully moved to ' + item.title;

    if (dragData.type === DragDataTypes.event) {
      const ids = this.getIds(dragData.data);
      moveItemsIntoContainer(this.eventsService, dragData.data, () =>
        this.recurrenceEditModalService
          .askForItems(dragData.data as CalendarEventSummaryDto[], 'Move recurring event?')
          .pipe(
            take(1),
            switchMap((recurrenceEditMode) =>
              this.eventsService.bunchUpdate(
                { ids },
                { calendarId: item.id },
                { message, tenantItems: dragData.data as CalendarEventSummaryDto[] },
                recurrenceEditMode,
              ),
            ),
          )
          .subscribe(),
      );
    } else if (dragData.type === DragDataTypes.calendar) {
      this.calendarsService.bunchUpdate(
        { ids: this.getIds(dragData.data) },
        { parentId: item.id },
        { message, tenantItems: dragData.data as CalendarSummaryDto[] },
      );
    }
  }

  public handleDuplicate(dragData: DragData, calendar: Calendar) {
    if (dragData.type === DragDataTypes.event) {
      this.recurrenceEditModalService
        .askForItems(dragData.data as CalendarEventSummaryDto[], 'Copy recurring event?')
        .pipe(
          take(1),
          switchMap((recurrenceEditMode) =>
            this.eventsService.duplicate(
              dragData.data as CalendarEventSummaryDto[],
              calendar.id,
              undefined,
              recurrenceEditMode,
            ),
          ),
        )
        .subscribe();
    } else if (dragData.type === DragDataTypes.calendar) {
      this.calendarsService
        .duplicate(dragData.data as CalendarSummaryDto[])
        .pipe(take(1))
        .subscribe();
    }
  }
}
